import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, EventEmitter, Input, OnChanges, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { distinctUntilChanged } from 'rxjs/operators';
import { formatCurrency } from '@angular/common';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { isEqual } from 'lodash-es';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { NON_DIGITS_PATTERN } from '@core-constants/regular-expressions/regular-expressions.constants';
import { RpcInputOptions } from '@core-controls/components/rpc-input/models/rpc-input-options';
import { RangeType } from '@core-controls/components/rpc-range-slider/rpc-range-slider.component';
import { SimpleChanges } from '@core-models/utilities/generic-simple-changes';
import { ShortNumberPipe } from '@core-pipes/short-number/short-number.pipe';
import { CurrencyConstants } from '@core-constants/currency/currency.constants';
import { RpcInputComponent } from '../../../core-modules/controls/components/rpc-input/rpc-input.component';
import { FormatCurrencyDirective } from '../../../core-modules/directives/format-currency/format-currency.directive';
import { RpcRangeSliderComponent } from '../../../core-modules/controls/components/rpc-range-slider/rpc-range-slider.component';

@Component({
    selector: 'range-filter',
    templateUrl: './range-filter.component.html',
    styleUrls: ['./range-filter.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [RpcInputComponent, FormatCurrencyDirective, RpcRangeSliderComponent]
})
export class RangeFilterComponent implements OnInit, OnChanges, AfterViewInit {

    @Input() public readonly min: number;
    @Input() public readonly max: number;
    @Input() public readonly sliderMin: number;
    @Input() public readonly sliderMax: number;
    @Input() public readonly selectedValue: { min?: number, max?: number };
    @Input() public readonly step: number;
    @Input() public readonly minFormControlName: string;
    @Input() public readonly maxFormControlName: string;
    @Input() public readonly anyMinAllowed: boolean = true;
    @Input() public readonly anyMaxAllowed: boolean = true;
    @Input() public readonly isCurrency: boolean = false;

    @Output() public valueChanged = new EventEmitter<{ min: number; max: number; }>();

    public readonly anyDefaultText = new TranslatePipe(this.translateService, this.changeDetectorRef).transform('RANGE_FILTER.TITLES.ANY') as string;
    public form: FormGroup;
    public minControlOptions: RpcInputOptions;
    public maxControlOptions: RpcInputOptions;

    public rangeSliderValue: { min: number; max: number; };

    private isInitialized = false;
    private previousValue: { min: number | null; max: number | null; };

    constructor(
        private readonly translateService: TranslateService,
        private readonly changeDetectorRef: ChangeDetectorRef,
        private readonly formBuilder: FormBuilder,
        private readonly destroyRef: DestroyRef
    ) { }

    public get minValue(): number {
        return this.anyMinAllowed ? Math.max(this.min - this.step, 0) : this.min;
    }

    public get maxValue(): number {
        return this.anyMaxAllowed ? (this.max + this.step) : this.max;
    }

    public ngOnInit(): void {
        this.form = this.formBuilder.group({});
        this.initializeForm();

        this.form.valueChanges.pipe(
            distinctUntilChanged(isEqual),
            takeUntilDestroyed(this.destroyRef)
        ).subscribe(() => {
            const { min, max } = this.getFormMinMaxValues();

            this.onFormChanged(min, max);
        });
    }

    public ngOnChanges(changes: SimpleChanges<RangeFilterComponent>): void {
        if (changes.selectedValue != null && !changes.selectedValue?.firstChange) {
            const updatedMinValue: number = changes.selectedValue?.currentValue?.min;
            const updatedMaxValue: number = changes.selectedValue?.currentValue?.max;

            this.onFormChanged(updatedMinValue, updatedMaxValue);
        }
    }

    public ngAfterViewInit(): void {
        this.isInitialized = true;
    }

    public onRangeSliderChanged({ min, max }: RangeType): void {
        const minChanged = min !== this.rangeSliderValue.min;
        const maxChanged = max !== this.rangeSliderValue.max;

        if (!minChanged && !maxChanged) {
            return;
        }

        const newMin = minChanged ? min === this.sliderMin ? null : min : this.selectedValue.min;
        const newMax = maxChanged ? max === this.sliderMax ? null : max : this.selectedValue.max;

        this.onFormChanged(newMin, newMax);
    }

    public formatBorderValues(): void {
        let { min, max } = this.getFormMinMaxValues();

        [min, max] = this.adjustFormBorderValues(min, max);

        this.onFormChanged(min, max);
    }

    public formatLabel = (value: number): string => {
        if (this.sliderMin === value && this.anyMinAllowed || this.sliderMax === value && this.anyMaxAllowed) {
            return this.anyDefaultText;
        }

        return new ShortNumberPipe().transform(value, this.isCurrency);
    };

    private initializeForm(): void {
        const formatInputLabel = (amount: number) => formatCurrency(
            amount,
            'en',
            CurrencyConstants.Symbols.USD,
            CurrencyConstants.Codes.USD,
            CurrencyConstants.Formats.WithoutFraction,
        );

        this.minControlOptions = new RpcInputOptions(
            this.form,
            this.minFormControlName,
            this.selectedValue?.min == null ? this.anyMinAllowed ? this.anyDefaultText : formatInputLabel(this.minValue) : formatInputLabel(this.selectedValue.min)
        );

        this.maxControlOptions = new RpcInputOptions(
            this.form,
            this.maxFormControlName,
            this.selectedValue?.max == null ? this.anyMaxAllowed ? this.anyDefaultText : formatInputLabel(this.max) : formatInputLabel(this.selectedValue.max)
        );

        this.rangeSliderValue = {
            min: this.selectedValue?.min == null ? this.sliderMin : this.selectedValue.min,
            max: this.selectedValue?.max == null ? this.sliderMax : this.selectedValue.max
        };

        this.previousValue = { min: this.selectedValue?.min, max: this.selectedValue?.max };
    }

    private onFormChanged(min: number, max: number, emitEvent = true): void {
        const minValueChanged = this.previousValue.min !== min;
        const maxValueChanged = this.previousValue.max !== max;

        this.rangeSliderValue = { min: min == null ? this.sliderMin : min, max: max != null && max < this.sliderMax ? max : this.sliderMax };
        this.previousValue = { min, max };

        if (minValueChanged || maxValueChanged) {
            this.notifyValueChanged(min, max);
        }

        const { updateMinControl, updateMaxControl } = this.shouldUpdateFormControls(min, max);

        if (updateMinControl || updateMaxControl) {
            this.patchForm(min, max, emitEvent, updateMinControl, updateMaxControl);
        }
    }

    private notifyValueChanged(min: number, max: number): void {
        if (this.isInitialized) {
            this.valueChanged.next({ min, max });
        }
    }

    private patchForm(min: number, max: number, emitEvent: boolean, minFormChanged: boolean, maxFormChanged: boolean): void {
        let resultMin: string | number = min;
        let resultMax: string | number = max;

        if (this.anyMinAllowed && (min == null || this.minValue === min)) {
            resultMin = this.anyDefaultText;
        }

        if (this.anyMaxAllowed && (max == null || max >= this.max)) {
            resultMax = this.anyDefaultText;
        }

        const patchValue = {};

        if (minFormChanged) {
            patchValue[this.minFormControlName] = resultMin;
        }

        if (maxFormChanged) {
            patchValue[this.maxFormControlName] = resultMax;
        }

        this.form.patchValue(patchValue, { emitEvent });
    }

    private adjustFormBorderValues(min: number | null, max: number | null): [number, number] {
        let [updatedMin, updatedMax] = [min, max];

        if (min != null && min > this.max) {
            updatedMin = this.max;
        }

        if (min != null && max != null && min > max) {
            [updatedMin, updatedMax] = [max, min];
        }

        return [updatedMin, updatedMax];
    }

    private convertToValidFilterValue(value: string | null, isValueInAllowedRange: (value: number) => boolean): number | null {
        const result = value?.toString().replace(NON_DIGITS_PATTERN, '');

        return !result || result === this.anyDefaultText || !isValueInAllowedRange(+result) ? null : +result;
    }

    private getFormMinMaxValues(): { min: number | null, max: number | null } {
        const { minValueText, maxValueText } = this.getRawFormValue();

        const min = this.convertToValidFilterValue(minValueText.toString(), value => value > this.minValue);
        const max = this.convertToValidFilterValue(maxValueText.toString(), value => value > this.minValue && value < this.maxValue);

        return { min, max };
    }

    private getRawFormValue(): { minValueText: string, maxValueText: string } {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
        const minValueText: string | number | null = this.form.value[this.minFormControlName];
        // eslint-disable-next-line 
        const maxValueText: string | number | null = this.form.value[this.maxFormControlName];

        return { minValueText: (minValueText ?? '').toString(), maxValueText: (maxValueText ?? '').toString() };
    }

    private shouldUpdateFormControls(min: number | null, max: number | null): { updateMinControl: boolean, updateMaxControl: boolean } {
        const { minValueText, maxValueText } = this.getRawFormValue();

        const expectedMinText = min == null ? (this.anyMinAllowed ? this.anyDefaultText : '') : min.toString();
        const expectedMaxText = max == null ? (this.anyMaxAllowed ? this.anyDefaultText : '') : max.toString();

        const updateMinControl = this.formatPresentationValue(minValueText) !== expectedMinText;
        const updateMaxControl = this.formatPresentationValue(maxValueText) !== expectedMaxText;

        return { updateMinControl, updateMaxControl };
    }

    private formatPresentationValue(value: string) {
        return value.replace(/\$|,|(\.\d*\D*)/g, '');
    }
}